import groupBy from 'lodash/groupBy';

import { pluralizeToString } from '~/utils/stringUtils';

import { PRICING_OPTION_LABELS, previewName } from '../constants';
import { BLACKOUT_NAME_TOO_LONG, validateBlackout } from '../tabs/blackouts/blackoutsValidation';
import type { BlackoutDraft } from '../tabs/blackouts/types';
import { findInclusionProblems } from '../tabs/inclusions/inclusionsModel';
import type { InclusionDraftPayload } from '../tabs/inclusions/types';
import { invalidOverrides } from '../tabs/overrides/overridesValidation';
import type { OverridesValues } from '../tabs/overrides/types';
import { DEFAULT_OPTION_STATE, roomsMissingAllocation } from '../tabs/packageOptions/packageOptionsModel';
import type { OptionState } from '../tabs/packageOptions/types';
import { SURCHARGE_NAME_TOO_LONG, validateSurchargePeriod } from '../tabs/surcharges/surchargePeriodValidation';
import type { SurchargePeriod } from '../tabs/surcharges/types';
import type { PricingOptionIndex } from '../types';

const NAMES_SHOWN = 3;

const quoteName = (name: string) => (name ? `"${previewName(name)}"` : 'unnamed');

function summarizeNames(names: Array<string>): string {
  const shown = names.slice(0, NAMES_SHOWN).map(quoteName).join(', ');
  return names.length > NAMES_SHOWN ? `${shown} and ${names.length - NAMES_SHOWN} more` : shown;
}

interface RequiredItemsInput {
  preferredOption: PricingOptionIndex;
  inclusionDrafts: Array<InclusionDraftPayload>;
  optionStates: Record<number, OptionState>;
  overrides: OverridesValues;
  blackouts: Array<BlackoutDraft>;
  surchargePeriods: ReadonlyArray<SurchargePeriod>;
}

// Long names are left out at submit instead of blocking it, so their length errors are dropped here.
export function findMissingRequiredItems({
  preferredOption,
  inclusionDrafts,
  optionStates,
  overrides,
  blackouts,
  surchargePeriods,
}: RequiredItemsInput): Array<string> {
  const inclusionProblems = Object.values(
    groupBy(findInclusionProblems(inclusionDrafts, optionStates), ({ pricingOptionIndex, missing }) =>
      [pricingOptionIndex, ...missing].join('|'),
    ),
  );
  const roomsWithoutAllocation = roomsMissingAllocation(optionStates[preferredOption] ?? DEFAULT_OPTION_STATE);
  return [
    ...(roomsWithoutAllocation.length > 0
      ? [
          `${PRICING_OPTION_LABELS[preferredOption]} – missing allocation (${pluralizeToString('room', roomsWithoutAllocation.length)}): ${summarizeNames(roomsWithoutAllocation)}`,
        ]
      : []),
    ...inclusionProblems.map((problems) => {
      const [{ pricingOptionIndex, missing }] = problems;
      const problem = missing.length > 0 ? `missing ${missing.join(', ')}` : 'applies to no package';
      return `${PRICING_OPTION_LABELS[pricingOptionIndex]} – ${problem} (${pluralizeToString('inclusion', problems.length)}): ${summarizeNames(problems.map(({ name }) => name))}`;
    }),
    ...invalidOverrides(overrides),
    ...blackouts.flatMap((blackout) =>
      validateBlackout(blackout)
        .filter((error) => error !== BLACKOUT_NAME_TOO_LONG)
        .map((error) => `Blackout ${summarizeNames([blackout.name])}: ${error}`),
    ),
    ...surchargePeriods
      // Unset options can't say whether the period applies, so it is listed rather than skipped.
      .filter((period) => period.options == null || period.options.includes(preferredOption))
      .flatMap((period) =>
        validateSurchargePeriod(period)
          .filter((error) => error !== SURCHARGE_NAME_TOO_LONG)
          .map((error) => `Surcharge period ${summarizeNames([period.name])}: ${error}`),
      ),
  ];
}
